import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, Redirect, useLocation, useHistory } from "react-router-dom";
import SessionManager from "../Utils/SessionManager";
import axios from "axios";
import "../css/css.css";
import "../css/Admin_css/admin-customize.css";
import COLORS from "../resources/colors/COLORS";
import eduokLogo from "../resources/images/eduok-icon.png";
import myToast from "../Utils/MyToast";

const ADMIN_LOGIN_TYPE = process.env.REACT_APP_LOGIN_TYPE_ADMIN;
const CLERK_LOGIN_TYPE = process.env.REACT_APP_LOGIN_TYPE_CLERK;

/** CRM side-nav style + EduOK primary blues (not pesto) */
const N = {
  deep: COLORS.primary_color_shade9,
  mid: COLORS.primary_color_shade8,
  bright: COLORS.primary_color_shade7,
  light: COLORS.primary_color_shade5,
  headerGradient: `linear-gradient(120deg, ${COLORS.primary_color_shade9} 0%, ${COLORS.primary_color_shade8} 42%, ${COLORS.primary_color_shade7} 100%)`,
  sidebarGradient: `linear-gradient(180deg, ${COLORS.primary_color_shade8} 0%, ${COLORS.primary_color_shade7} 38%, ${COLORS.primary_color_shade9} 100%)`,
  border: "rgba(255,255,255,0.1)",
  borderSoft: "rgba(255,255,255,0.07)",
  glass: "rgba(255,255,255,0.08)",
  glassHover: "rgba(255,255,255,0.14)",
  glassBorder: "rgba(255,255,255,0.12)",
  text: "rgba(255,255,255,0.94)",
  textMuted: "rgba(255,255,255,0.72)",
  accent: COLORS.amber6,
  accentDeep: COLORS.amber8,
  accentSoft: "rgba(255,179,0,0.28)",
  accentSofter: "rgba(255,179,0,0.12)",
  accentBorder: "rgba(255,179,0,0.4)",
  label: COLORS.amber2,
  ink: COLORS.primary_color_shade9,
  shadow: "0 8px 24px rgba(1, 87, 155, 0.28)",
  drawerWidth: 248,
  headerHeight: 72,
};

/** Crisp Font Awesome icons (CRM-style vectors) by component / module name */
// Created By Cursor
function resolveFaIcon(item) {
  const key = `${item?.component_name || ""} ${item?.module_name || ""}`.toLowerCase();
  if (/dashboard|home/.test(key)) return "fa-home";
  //Created By Cursor — leave before staff/attendance; use FA4 icon present in all builds
  if (/leave/.test(key)) return "fa-suitcase";
  if (/student|admission|enroll/.test(key)) return "fa-graduation-cap";
  if (/staff|employee|payroll/.test(key)) return "fa-users";
  if (/attendance/.test(key)) return "fa-check-square-o";
  if (/fee|account|finance|ledger|receipt|collect/.test(key)) return "fa-rupee";
  if (/exam|test|mark|result|academic/.test(key)) return "fa-graduation-cap";
  if (/timetable|schedule|period/.test(key)) return "fa-calendar";
  if (/transport|vehicle|bus/.test(key)) return "fa-bus";
  if (/library|book/.test(key)) return "fa-book";
  if (/inventory|store|stock/.test(key)) return "fa-cubes";
  if (/sms|whatsapp|message|notification/.test(key)) return "fa-commenting";
  if (/report|analytic/.test(key)) return "fa-bar-chart";
  if (/setting|config|privilege|manage.?record/.test(key)) return "fa-cogs";
  if (/hostel/.test(key)) return "fa-building";
  if (/certificate|id.?card/.test(key)) return "fa-id-card";
  if (/gallery|media|photo/.test(key)) return "fa-picture-o";
  if (/event|activity/.test(key)) return "fa-flag";
  if (/homework|assignment/.test(key)) return "fa-pencil-square-o";
  if (/cms|website|content/.test(key)) return "fa-globe";
  if (/salary|wage/.test(key)) return "fa-money";
  return "fa-th-large";
}

const NavMenuAdmin = () => {
  const location = useLocation();
  const history = useHistory();
  const [loggedIn, setLoggedIn] = useState(!!localStorage.getItem("token"));
  const [staffName, setStaffName] = useState("");
  const [staffRole, setStaffRole] = useState("");
  const [staffImagePath, setStaffImagePath] = useState("");
  const [loginType, setLoginType] = useState("");
  const [superAdminModules, setSuperAdminModules] = useState([]);
  const [erpModules, setErpModules] = useState([]);
  const [lmsModules, setLmsModules] = useState([]);
  const [schoolDetails, setSchoolDetails] = useState([]);
  const [schoolLogoBroken, setSchoolLogoBroken] = useState(false);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    setLoggedIn(false);
  }, []);

  //Created By Cursor — SPA navigate so AdminMain Switch can open module routes
  const changePage = useCallback(
    (path) => {
      if (!path) return;
      if (location?.pathname === path) return;
      history.push(path);
    },
    [history, location]
  );

  const isPathActive = useCallback(
    (path) => {
      const pathname = location?.pathname || window.location.pathname || "";
      if (path === "/AdminMain") return pathname === "/AdminMain" || pathname === "/AdminMain/";
      return pathname === path || pathname.startsWith(`${path}/`);
    },
    [location]
  );

  //Created By Cursor — split clerk modules like AdminMain dashboard sections
  const applyClerkModules = useCallback((list) => {
    const arr = Array.isArray(list) ? list : [];
    setErpModules(
      arr.filter(
        (m) => m?.software_section === "ERP" || !m?.software_section
      )
    );
    setLmsModules(arr.filter((m) => m?.software_section === "LMS"));
  }, []);

  const fetchAdminModules = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      myToast.notify_error("School code is missing. Please login again.");
      setLoggedIn(false);
      return;
    }

    const url =
      process.env.REACT_APP_SERVER_NAME +
      process.env.REACT_APP_TARGET_DIR +
      "getAllAdminModules.php?";

    try {
      const { data } = await axios.get(url, {
        params: {
          school_code: schoolCode,
          system_code: process.env.REACT_APP_SYSTEM_CODE,
        },
      });
      if (data?.api_status === "OK") {
        setSuperAdminModules(data.data || []);
      } else {
        myToast.notify_warning(data?.api_remark || "Failed to load modules");
      }
    } catch (error) {
      myToast.notify_error(
        error.response?.data?.api_remark || "Network error. Please check your connection."
      );
    }
  }, []);

  const fetchClerkModules = useCallback(async () => {
    const url =
      process.env.REACT_APP_SERVER_NAME +
      process.env.REACT_APP_TARGET_DIR +
      "getAllActiveModulesOfClerk.php?";

    try {
      const { data } = await axios.get(url, {
        params: { system_code: process.env.REACT_APP_SYSTEM_CODE },
      });
      if (data?.api_status === "OK") {
        applyClerkModules(data.data || data);
      } else {
        myToast.notify_warning(data?.api_remark || "Failed to load modules");
      }
    } catch (error) {
      myToast.notify_error(
        error.response?.data?.api_remark || "Network error. Please check your connection."
      );
    }
  }, [applyClerkModules]);

  const fetchSchoolDetails = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      myToast.notify_error("School code is missing. Please login again.");
      return;
    }

    const url =
      process.env.REACT_APP_SERVER_NAME +
      process.env.REACT_APP_TARGET_DIR +
      "getSchoolDetails.php?";

    try {
      const { data } = await axios.get(url, { params: { school_code: schoolCode } });
      if (data?.api_status === "OK") {
        const payload = data.data ?? data;
        //Created By Cursor — normalize array or single object
        setSchoolDetails(Array.isArray(payload) ? payload : payload ? [payload] : []);
        setSchoolLogoBroken(false);
      } else {
        myToast.notify_warning(data?.api_remark || "Failed to load school details");
      }
    } catch (error) {
      myToast.notify_error(
        error.response?.data?.api_remark || "Network error. Please check your connection."
      );
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoggedIn(false);
      return;
    }

    const staffData = SessionManager.getStaffDetails();
    if (!staffData || !staffData.login_type) {
      setLoggedIn(false);
      return;
    }

    setStaffName(staffData.staff_name || "");
    setStaffRole(staffData.staff_role || "");
    setStaffImagePath(staffData.staff_image_path || "");
    setLoginType(staffData.login_type);

    //Created By Cursor — Admin also loads ERP ("Admin / modules") into side nav
    if (staffData.login_type === ADMIN_LOGIN_TYPE) {
      fetchAdminModules();
      fetchClerkModules();
    } else if (staffData.login_type === CLERK_LOGIN_TYPE) {
      fetchClerkModules();
    }

    fetchSchoolDetails();
  }, [fetchAdminModules, fetchClerkModules, fetchSchoolDetails]);

  //Created By Cursor — path for Super Admin vs clerk ERP/LMS (module_id)
  const buildNavPath = useCallback((item, withModuleId) => {
    const name = item?.component_name;
    if (!name) return "";
    if (withModuleId && item?.module_id) {
      return `/AdminMain/${name}/${item.module_id}`;
    }
    return `/AdminMain/${name}`;
  }, []);

  const renderNavModule = useCallback(
    (item, index, withModuleId) => {
      const path = buildNavPath(item, withModuleId);
      if (!path) return null;
      const active = isPathActive(path);
      const fa = resolveFaIcon(item);
      return (
        <li
          key={`${item.module_id || item.component_name || "mod"}-${index}`}
          className={`crm-nav-item${active ? " is-active" : ""}`}
        >
          <Link
            to={path}
            onClick={(e) => {
              e.preventDefault();
              changePage(path);
            }}
          >
            <span className="crm-nav-icon" aria-hidden>
              <i className={`fa ${fa}`} />
            </span>
            <span>{item.module_name}</span>
          </Link>
        </li>
      );
    },
    [buildNavPath, changePage, isPathActive]
  );

  const school = Array.isArray(schoolDetails)
    ? schoolDetails[0]
    : schoolDetails || null;
  const schoolName =
    school?.school_name ||
    school?.School_Name ||
    school?.name ||
    "";
  const schoolAddress =
    school?.school_address ||
    school?.School_Address ||
    school?.address ||
    "";
  const schoolLogo =
    school?.school_logo_path ||
    school?.School_Logo_Path ||
    school?.logo ||
    "";
  const navStyles = useMemo(
    () => `
      .crm-nav-header {
        background: ${N.headerGradient} !important;
        box-shadow: ${N.shadow};
        border-bottom: 1px solid ${N.border};
        height: ${N.headerHeight}px !important;
        min-height: ${N.headerHeight}px !important;
        max-height: ${N.headerHeight}px !important;
        overflow: hidden !important;
        position: fixed !important;
        left: 0 !important;
        right: 0 !important;
        top: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
        z-index: 1035;
      }
      /*Created By Cursor — align sidebar + content under fixed ${N.headerHeight}px header */
      .wrap > .page-body {
        padding-top: 0 !important;
        overflow: hidden !important;
      }
      .wrap .page-body .left-sidebar,
      html.fixed .left-sidebar {
        top: ${N.headerHeight}px !important;
        position: fixed !important;
        left: 0 !important;
        bottom: 0 !important;
        height: calc(100vh - ${N.headerHeight}px) !important;
        overflow: hidden !important;
      }
      /*Created By Cursor — side nav must scroll when menu is long */
      .wrap .page-body .crm-sidebar,
      .crm-sidebar,
      #left-nav.crm-sidebar,
      #left-nav.nano {
        height: calc(100vh - ${N.headerHeight}px) !important;
        max-height: calc(100vh - ${N.headerHeight}px) !important;
        overflow-y: auto !important;
        overflow-x: hidden !important;
        -webkit-overflow-scrolling: touch;
      }
      .crm-sidebar .nano-content,
      #left-nav .nano-content {
        position: relative !important;
        top: auto !important;
        right: auto !important;
        bottom: auto !important;
        left: auto !important;
        height: auto !important;
        max-height: none !important;
        overflow: visible !important;
      }
      .content {
        margin-top: ${N.headerHeight}px !important;
        margin-left: ${N.drawerWidth}px !important;
        padding-top: 52px !important;
        min-height: calc(100vh - ${N.headerHeight}px) !important;
        box-sizing: border-box !important;
      }
      html.fixed .content {
        margin-top: ${N.headerHeight}px !important;
      }
      html.fixed .content-header,
      .content .content-header {
        position: fixed !important;
        top: ${N.headerHeight}px !important;
        left: ${N.drawerWidth}px !important;
        right: 0 !important;
        height: 40px !important;
        z-index: 1010;
        margin: 0 !important;
      }
      .crm-nav-header > section {
        width: 100%;
        max-width: 100%;
        height: 100%;
        margin: 0;
        padding: 0;
        overflow: hidden;
      }
      .crm-nav-header .crm-header-bar {
        display: flex !important;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        height: ${N.headerHeight}px;
        min-height: ${N.headerHeight}px;
        max-height: ${N.headerHeight}px;
        margin: 0 !important;
        padding: 0 16px;
        gap: 12px;
        box-sizing: border-box;
        overflow: hidden;
      }
      .crm-nav-header .crm-header-brand,
      .crm-nav-header .crm-header-user {
        flex: 1 1 0;
        min-width: 0;
        display: flex;
        align-items: center;
        float: none !important;
        width: auto !important;
        max-width: none !important;
        margin: 0 !important;
        padding: 0 !important;
        position: static !important;
        transform: none !important;
      }
      .crm-nav-header .crm-header-brand {
        justify-content: flex-start;
      }
      .crm-nav-header .crm-header-center {
        flex: 0 1 auto;
        max-width: 48%;
        min-width: 0;
        text-align: center;
        float: none !important;
        margin: 0 !important;
        padding: 0 8px !important;
        position: static !important;
        transform: none !important;
      }
      .crm-school-block {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        max-width: 100%;
        min-width: 0;
      }
      .crm-school-logo {
        width: 44px;
        height: 44px;
        object-fit: contain;
        border-radius: 10px;
        background: rgba(255, 255, 255, 0.95);
        border: 1px solid rgba(255, 255, 255, 0.55);
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
        flex-shrink: 0;
        display: block;
      }
      .crm-school-copy {
        min-width: 0;
        text-align: left;
      }
      .crm-nav-header .crm-header-user {
        justify-content: flex-end;
      }
      .crm-nav-header .leftside-header {
        display: flex !important;
        align-items: center;
        gap: 10px;
        width: auto !important;
        float: none !important;
        margin: 0 !important;
      }
      .crm-nav-header .leftside-header .logo {
        width: auto !important;
        height: auto !important;
        display: flex !important;
        align-items: center;
        margin: 0 !important;
        float: none !important;
      }
      .crm-nav-header .leftside-header .logo img,
      .crm-nav-header .crm-logo-img {
        width: 36px !important;
        height: 36px !important;
        padding: 0 !important;
        margin: 0 !important;
        object-fit: contain;
        border-radius: 8px;
        display: block;
        flex-shrink: 0;
      }
      .crm-nav-header .logo a,
      .crm-logo-link {
        display: inline-flex !important;
        align-items: center;
        gap: 6px;
        background: ${N.glass};
        border: 1px solid ${N.glassBorder};
        padding: 4px 6px !important;
        border-radius: 10px;
        position: static !important;
        color: #fff !important;
        font-size: 14px !important;
        font-family: inherit !important;
        transition: background 0.18s ease, border-color 0.18s ease;
        text-decoration: none !important;
      }
      .crm-logo-link:hover,
      .crm-nav-header .logo a:hover {
        background: ${N.glassHover};
        border-color: ${N.accentBorder};
      }
      .crm-product-name {
        margin: 0;
        color: #fff;
        font-weight: 750;
        font-size: 14px;
        letter-spacing: 0.02em;
        line-height: 1.2;
        white-space: nowrap;
      }
      .crm-school-name {
        margin: 0;
        color: #fff;
        font-weight: 750;
        font-size: 18px;
        letter-spacing: 0.02em;
        line-height: 1.25;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .crm-school-addr {
        margin: 2px 0 0;
        color: ${N.textMuted};
        font-size: 12px;
        font-weight: 500;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .crm-nav-header .rightside-header {
        float: none !important;
        height: auto !important;
        margin: 0 !important;
        display: flex !important;
        align-items: center;
        justify-content: flex-end;
        gap: 10px;
        width: auto !important;
        max-width: 100%;
      }
      /*Created By Cursor — beat .page-header .rightside-header .header-section { padding: 0 4px } */
      .crm-nav-header .rightside-header .header-section.crm-user-header,
      .page-header.crm-nav-header #user-headerbox.crm-user-header {
        background: ${N.glass} !important;
        border: 1px solid ${N.glassBorder} !important;
        border-radius: 10px !important;
        padding: 4px 6px !important;
        transition: background 0.18s ease, border-color 0.18s ease;
        cursor: pointer;
        width: auto !important;
        max-width: none !important;
        flex-shrink: 0;
        display: inline-flex !important;
        align-items: center;
        vertical-align: middle;
        box-sizing: border-box !important;
      }
      .crm-nav-header .rightside-header .header-section.crm-user-header:hover,
      .page-header.crm-nav-header #user-headerbox.crm-user-header:hover {
        background: ${N.glassHover} !important;
        border-color: ${N.accentBorder} !important;
      }
      .crm-nav-header #user-headerbox .user-header-wrap {
        display: flex !important;
        align-items: center;
        gap: 8px;
        width: auto !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      .crm-nav-header #user-headerbox .user-header-wrap .user-photo {
        width: auto !important;
        margin: 0 !important;
        overflow: visible !important;
        border-radius: 0 !important;
        flex-shrink: 0;
        display: block !important;
      }
      .crm-nav-header #user-headerbox .user-header-wrap .user-photo img,
      .crm-user-photo {
        width: 32px !important;
        height: 32px !important;
        border-radius: 50% !important;
        border: 2px solid rgba(255,176,32,0.45) !important;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        object-fit: cover;
        display: block !important;
      }
      .crm-nav-header #user-headerbox .user-header-wrap .user-info {
        display: flex !important;
        flex-direction: column;
        justify-content: center;
        width: auto !important;
        max-width: none !important;
        margin: 0 !important;
        padding: 0 !important;
        flex: 0 0 auto;
      }
      .crm-nav-header #user-headerbox .user-header-wrap .user-info span.user-name,
      .crm-nav-header #user-headerbox .user-header-wrap .user-info span.user-profile {
        width: auto !important;
        max-width: none !important;
        white-space: nowrap;
        line-height: 1.2 !important;
      }
      .crm-nav-header #user-headerbox .user-header-wrap > i {
        padding: 0 0 0 4px !important;
        margin: 0 !important;
        line-height: 1 !important;
      }
      @media (max-width: 900px) {
        .crm-nav-header .crm-header-bar {
          gap: 8px;
          padding: 6px 10px;
        }
        .crm-product-name {
          font-size: 13px;
        }
        .crm-school-name {
          font-size: 15px;
        }
        .crm-logout-btn {
          padding: 8px 10px;
        }
      }
      @media (max-width: 767px) {
        .crm-nav-header {
          position: fixed !important;
          overflow: hidden !important;
        }
        .crm-nav-header .leftside-header,
        .crm-nav-header .rightside-header {
          position: static !important;
          margin-top: 0 !important;
          background: transparent !important;
          float: none !important;
          width: auto !important;
        }
        .wrap > .page-body {
          padding-top: 0 !important;
          overflow: hidden !important;
        }
        .content {
          margin-top: ${N.headerHeight}px !important;
          margin-left: 0 !important;
          padding-top: 52px !important;
        }
        html.fixed .content-header,
        .content .content-header {
          left: 0 !important;
        }
      }
      .crm-logout-btn {
        background: ${N.glass};
        border: 1px solid ${N.glassBorder};
        border-radius: 12px;
        padding: 8px 14px;
        color: #fff;
        cursor: pointer;
        font-weight: 650;
        font-size: 13px;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        transition: background 0.18s ease, border-color 0.18s ease, transform 0.18s ease;
      }
      .crm-logout-btn:hover {
        background: ${N.accentSoft};
        border-color: ${N.accentBorder};
        color: ${N.accent};
        transform: translateY(-1px);
      }
      .crm-menu-toggle {
        background: ${N.glass};
        border: 1px solid ${N.glassBorder};
        border-radius: 10px;
        padding: 8px 12px;
        color: #fff;
        cursor: pointer;
        transition: background 0.18s ease, color 0.18s ease;
      }
      .crm-menu-toggle:hover {
        background: ${N.glassHover};
        color: ${N.accent};
      }
      .left-sidebar {
        width: ${N.drawerWidth}px !important;
      }
      .crm-sidebar {
        width: ${N.drawerWidth}px !important;
        background: ${N.sidebarGradient} !important;
        border-right: 1px solid ${N.border};
        box-shadow: inset -1px 0 0 rgba(255,255,255,0.04);
        height: calc(100vh - ${N.headerHeight}px) !important;
        overflow-y: auto !important;
        overflow-x: hidden !important;
        position: relative;
      }
      .crm-sidebar::before {
        content: "";
        position: absolute;
        inset: 0;
        pointer-events: none;
        background: radial-gradient(ellipse 90% 40% at 0% 0%, rgba(3,169,244,0.12) 0%, transparent 55%);
        z-index: 0;
      }
      .crm-sidebar .nano-content {
        position: relative;
        z-index: 1;
        padding: 10px 8px !important;
        height: auto !important;
        overflow: visible !important;
      }
      .crm-sidebar .nano-content nav {
        height: auto !important;
      }
      .crm-sidebar .nano-content ul.nav {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .crm-nav-label {
        margin: 10px 2px 2px;
        padding: 0 6px;
        font-size: 8.5px;
        font-weight: 750;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: ${N.label};
        opacity: 0.95;
      }
      .crm-nav-item {
        margin: 0;
        list-style: none;
        border: none !important;
        border-bottom: none !important;
        box-shadow: none !important;
      }
      ul#main-nav > li,
      ul#main-nav > li.crm-nav-item,
      ul#main-nav > li.crm-nav-label {
        border: none !important;
        border-bottom: none !important;
        box-shadow: none !important;
      }
      ul#main-nav > li.open-item > a,
      ul#main-nav > li.active-item > a {
        box-shadow: none !important;
      }
      .crm-nav-item a {
        width: 100%;
        display: flex !important;
        align-items: center !important;
        gap: 8px;
        padding: 7px 9px !important;
        border-radius: 14px;
        border: 1px solid ${N.borderSoft} !important;
        background: ${N.glass} !important;
        color: ${N.text} !important;
        text-decoration: none !important;
        font-size: 15px;
        font-weight: 500;
        text-align: left;
        backdrop-filter: blur(8px);
        box-shadow: none !important;
        transition: transform 0.18s ease, background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
        white-space: nowrap;
        min-width: 0;
      }
      .crm-nav-item a:hover {
        transform: translateX(2px);
        background: ${N.glassHover} !important;
        border-color: ${N.glassBorder} !important;
        color: #fff !important;
      }
      .crm-nav-item.is-active a {
        background: linear-gradient(135deg, ${N.accentSoft} 0%, ${N.accentSofter} 100%) !important;
        border-color: ${N.accentBorder} !important;
        color: ${N.accent} !important;
        font-weight: 650;
        box-shadow: 0 4px 12px rgba(255,179,0,0.16) !important;
      }
      .crm-nav-item.is-active a:hover {
        background: linear-gradient(135deg, ${N.accentSoft} 0%, ${N.accentSofter} 100%) !important;
        border-color: ${N.accentBorder} !important;
      }
      .crm-nav-icon {
        width: 28px;
        height: 28px;
        border-radius: 9px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        background: rgba(255, 255, 255, 0.88);
        color: #0288d1 !important;
        font-size: 13px;
        border: 1px solid rgba(255, 255, 255, 0.55);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
      }
      .crm-nav-item:nth-child(7n + 1) .crm-nav-icon {
        color: #0288d1 !important;
      }
      .crm-nav-item:nth-child(7n + 2) .crm-nav-icon {
        color: #ef6c00 !important;
      }
      .crm-nav-item:nth-child(7n + 3) .crm-nav-icon {
        color: #2e7d32 !important;
      }
      .crm-nav-item:nth-child(7n + 4) .crm-nav-icon {
        color: #c2185b !important;
      }
      .crm-nav-item:nth-child(7n + 5) .crm-nav-icon {
        color: #7b1fa2 !important;
      }
      .crm-nav-item:nth-child(7n + 6) .crm-nav-icon {
        color: #e64a19 !important;
      }
      .crm-nav-item:nth-child(7n + 7) .crm-nav-icon {
        color: #00838f !important;
      }
      .crm-nav-item.is-active .crm-nav-icon {
        background: rgba(255, 255, 255, 0.95);
        color: ${N.accentDeep} !important;
        border-color: rgba(255, 179, 0, 0.65);
        box-shadow: 0 2px 8px rgba(255,179,0,0.28);
      }
      .crm-nav-icon i,
      .crm-nav-icon .fa {
        display: inline-block !important;
        line-height: 1;
        font-size: 13px !important;
        font-family: FontAwesome !important;
        font-style: normal !important;
        font-weight: normal !important;
        color: inherit !important;
        -webkit-font-smoothing: antialiased;
        speak: none;
        text-rendering: auto;
      }
      .crm-nav-icon .fa:before {
        font-family: FontAwesome !important;
      }
      ul#main-nav li span.crm-nav-icon {
        font-size: 13px !important;
        font-family: inherit;
      }
      .crm-nav-item a:hover .crm-nav-icon,
      .crm-nav-item.is-active a:hover .crm-nav-icon {
        background: rgba(255, 255, 255, 0.95);
      }
      ul#main-nav li span {
        vertical-align: middle;
        font-size: 15px;
      }
      .crm-nav-item a span:not(.crm-nav-icon) {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.25;
        font-size: 15px;
      }
      .crm-dropdown-menu {
        background: #fff;
        border-radius: 12px;
        box-shadow: 0 8px 24px rgba(0,45,40,0.18);
        border: 1px solid #e5e7eb;
        padding: 8px;
        margin-top: 8px;
      }
      .crm-dropdown-menu ul { list-style: none; padding: 0; margin: 0; }
      .crm-dropdown-menu li { margin: 4px 0; }
      .crm-dropdown-menu button {
        width: 100%;
        text-align: left;
        padding: 8px 12px;
        border-radius: 8px;
        border: none;
        background: transparent;
        color: #374151;
        font-size: 12px;
        font-weight: 500;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        gap: 10px;
      }
      .crm-dropdown-menu button:hover {
        background: linear-gradient(135deg, rgba(255,179,0,0.12) 0%, rgba(3,169,244,0.08) 100%);
        color: ${N.bright};
        transform: translateX(4px);
      }
      .crm-scroll-top {
        background: ${N.headerGradient};
        box-shadow: ${N.shadow};
        transition: all 0.3s ease;
      }
      .crm-scroll-top:hover {
        transform: translateY(-4px);
        box-shadow: 0 6px 16px rgba(0,45,40,0.35);
      }
    `,
    []
  );

  if (!loggedIn) {
    return <Redirect to="/" />;
  }

  return (
    <div className="wrap">
      <style>{navStyles}</style>
      <div className="page-header crm-nav-header" style={{ background: N.headerGradient }}>
        <section>
          <div className="crm-header-bar">
            <div className="crm-header-brand">
              <div className="leftside-header">
                <div className="logo">
                  <Link to="/AdminMain" className="logo-text crm-logo-link" title="EduOK ERP">
                    <img
                      src={eduokLogo}
                      alt="EduOK"
                      className="crm-logo-img"
                    />
                    <span className="crm-product-name">EduOK ERP</span>
                  </Link>
                </div>
                <div
                  id="menu-toggle"
                  className="visible-xs toggle-left-sidebar crm-menu-toggle"
                  data-toggle-class="left-sidebar-open"
                  data-target="html"
                >
                  <i className="fa fa-bars" aria-label="Toggle sidebar" />
                </div>
              </div>
            </div>
            <div className="crm-header-center">
              <div className="crm-school-block">
                {schoolLogo && !schoolLogoBroken ? (
                  <img
                    src={schoolLogo}
                    alt=""
                    className="crm-school-logo"
                    onError={() => setSchoolLogoBroken(true)}
                  />
                ) : null}
                <div className="crm-school-copy">
                  <h1 className="crm-school-name">
                    {schoolName || "School Management"}
                  </h1>
                  {schoolAddress ? (
                    <p className="crm-school-addr">{schoolAddress}</p>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="crm-header-user">
              <div className="rightside-header">
                <div className="header-section crm-user-header" id="user-headerbox">
                  <div className="user-header-wrap">
                    <div className="user-photo">
                      <img src={staffImagePath} alt="" className="crm-user-photo" />
                    </div>
                    <div className="user-info">
                      <span className="user-name" style={{ color: "#fff", fontWeight: 650, fontSize: 14 }}>
                        {staffName}
                      </span>
                      <span className="user-profile" style={{ color: N.textMuted, fontSize: 12 }}>
                        {staffRole}
                      </span>
                    </div>
                    <i className="fa fa-plus icon-open" aria-hidden="true" style={{ color: N.accent }} />
                    <i className="fa fa-minus icon-close" aria-hidden="true" style={{ color: N.accent }} />
                  </div>
                  <div className="user-options dropdown-box">
                    <div className="drop-content basic crm-dropdown-menu">
                      <ul>
                        <li>
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() => changePage("/AdminMain/MyProfile")}
                          >
                            <i className="fa fa-user" aria-hidden="true" />
                            Profile
                          </button>
                        </li>
                        <li>
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            title="Logout"
                            onClick={handleLogout}
                          >
                            <i className="fa fa-sign-out" aria-hidden="true" />
                            Logout
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <button type="button" className="crm-logout-btn" onClick={handleLogout}>
                  <i className="fa fa-sign-out" aria-hidden="true" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="page-body">
        <div className="left-sidebar">
          <div
            id="left-nav"
            className="nano crm-sidebar"
            style={{
              background: N.sidebarGradient,
              width: N.drawerWidth,
              height: `calc(100vh - ${N.headerHeight}px)`,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            <div className="nano-content" style={{ padding: "10px 8px", height: "auto" }}>
              <nav style={{ height: "auto" }}>
                <ul className="nav" id="main-nav">
                  <li className={`crm-nav-item${isPathActive("/AdminMain") ? " is-active" : ""}`}>
                    <Link
                      to="/AdminMain"
                      onClick={(e) => {
                        e.preventDefault();
                        changePage("/AdminMain");
                      }}
                    >
                      <span className="crm-nav-icon" aria-hidden>
                        <i className="fa fa-home" />
                      </span>
                      <span>Dashboard</span>
                    </Link>
                  </li>

                  {superAdminModules.length > 0 ? (
                    <li className="crm-nav-label" key="label-super-admin">
                      Super Admin modules
                    </li>
                  ) : null}
                  {superAdminModules.map((item, index) =>
                    renderNavModule(item, index, false)
                  )}

                  {erpModules.length > 0 ? (
                    <li className="crm-nav-label" key="label-admin-modules">
                      Admin / modules
                    </li>
                  ) : null}
                  {erpModules.map((item, index) =>
                    renderNavModule(item, index, true)
                  )}

                  {lmsModules.length > 0 ? (
                    <li className="crm-nav-label" key="label-lms">
                      Learning Management
                    </li>
                  ) : null}
                  {lmsModules.map((item, index) =>
                    renderNavModule(item, `lms-${index}`, true)
                  )}
                </ul>
              </nav>
            </div>
          </div>
        </div>

        <a href="#" className="scroll-to-top crm-scroll-top">
          <i className="fa fa-angle-double-up" />
        </a>
      </div>
    </div>
  );
};

export default NavMenuAdmin;
